import { useId, useRef, useState, type ReactNode } from 'react';
import { clsx } from 'clsx';

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /** The text. Keep it to a phrase — anything longer belongs on the page. */
  content: ReactNode;
  children: ReactNode;
  placement?: TooltipPlacement;
  /** Delay before it appears, so passing the cursor over a toolbar stays quiet. */
  delay?: number;
  className?: string;
  disabled?: boolean;
}

const PLACEMENT: Record<TooltipPlacement, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-1.5',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-1.5',
  left: 'right-full top-1/2 -translate-y-1/2 mr-1.5',
  right: 'left-full top-1/2 -translate-y-1/2 ml-1.5',
};

/**
 * A hint on hover or focus.
 *
 * Keyboard focus shows it as well as the pointer, and Escape dismisses it — a tooltip
 * that only answers to a mouse is decoration. It never carries information that is not
 * available elsewhere, so a touch user misses nothing.
 */
export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 350,
  className,
  disabled = false,
}: TooltipProps) {
  const id = useId();
  const [visible, setVisible] = useState(false);
  const timer = useRef<number | null>(null);

  const show = () => {
    if (disabled) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setVisible(true), delay);
  };

  const hide = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setVisible(false);
  };

  return (
    <span
      className={clsx('relative inline-flex', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocusCapture={() => !disabled && setVisible(true)}
      onBlurCapture={hide}
      onKeyDown={(event) => {
        if (event.key === 'Escape') hide();
      }}
    >
      <span aria-describedby={visible ? id : undefined} className="inline-flex">
        {children}
      </span>

      {visible && !disabled ? (
        <span
          role="tooltip"
          id={id}
          className={clsx(
            'pointer-events-none absolute z-150 w-max max-w-[16rem] rounded-sm px-2 py-1',
            'bg-ink text-micro leading-snug text-paper shadow-pop',
            'animate-fade-in',
            PLACEMENT[placement],
          )}
        >
          {content}
        </span>
      ) : null}
    </span>
  );
}
